/**
 * Shared body-scroll lock for WDrawer instances.
 *
 * Vuetify's <v-navigation-drawer temporary> uses an overlay underneath that
 * manages <html> overflow via inline style — and removes that inline style
 * every time any single drawer closes, regardless of whether other drawers
 * are still open. We use a CSS class with `overflow: hidden !important`
 * (defined in WDrawer.vue's unscoped <style> block) so `!important` beats
 * Vuetify's inline style.
 *
 * The counter lives at module scope so it's shared across every importer
 * automatically — required for the stack scenario where two drawers can
 * be open at once (e.g. operational-category SnapshotsDrawer +
 * EnhancedProfileSidebar on LocationDashboard).
 */

const SCROLL_LOCK_CLASS = 'w-drawer-scroll-lock'
const BASE_DRAWER_ORDER = -1

let openDrawerCount = 0
let nextDrawerOrder = BASE_DRAWER_ORDER
const activeDrawerOrders = new Set<number>()
const openDrawerClosers = new Set<() => void>()

export const acquireBodyScrollLock = (): void => {
  if (openDrawerCount === 0) {
    document.documentElement.classList.add(SCROLL_LOCK_CLASS)
  }
  openDrawerCount++
}

export const releaseBodyScrollLock = (): void => {
  openDrawerCount = Math.max(0, openDrawerCount - 1)
  if (openDrawerCount === 0) {
    document.documentElement.classList.remove(SCROLL_LOCK_CLASS)
  }
}

/**
 * Vuetify gives lower layout order values a higher z-index. Assign each newly
 * opened drawer a unique, decreasing order so nested drawers stack above the
 * drawers that launched them.
 */
export const acquireDrawerStackOrder = (): number => {
  const order = nextDrawerOrder
  nextDrawerOrder--
  activeDrawerOrders.add(order)
  return order
}

export const releaseDrawerStackOrder = (order: number): void => {
  activeDrawerOrders.delete(order)

  if (activeDrawerOrders.size === 0) {
    nextDrawerOrder = BASE_DRAWER_ORDER
  }
}

/** Registers an open teleported drawer with app-level overlay coordination. */
export const registerOpenDrawer = (close: () => void): void => {
  openDrawerClosers.add(close)
}

export const unregisterOpenDrawer = (close: () => void): void => {
  openDrawerClosers.delete(close)
}

/**
 * Closes every open WDrawer from the top of the stack down. The snapshot keeps
 * iteration stable while each controlled drawer synchronously updates its
 * parent and unregisters on the following Vue flush.
 */
export const closeOpenDrawers = (): number => {
  const closers = [...openDrawerClosers].reverse()
  closers.forEach((close) => close())
  return closers.length
}
